import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import SearchBar from './components/SearchBar';
import FeaturedSection from './components/FeaturedSection';
import CategoryFilter from './components/CategoryFilter';
import EventGrid from './components/EventGrid';
import LoadMoreButton from './components/LoadMoreButton';
import { useEvents } from '../../hooks/useEvents';
import { useNavigate } from 'react-router-dom';

const EventDiscovery = () => {
  // Memoize initial filters to prevent object reference changes
  const initialFilters = useMemo(() => ({}), []);
  
  const {
    events,
    featuredEvents,
    loading,
    error,
    totalCount,
    fetchEvents,
    fetchFeaturedEvents,
    searchEvents,
    getEventCategories
  } = useEvents(initialFilters);

  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    category: 'all',
    location: 'all',
    priceRange: 'all',
    dateRange: null
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [eventsPerPage] = useState(12);
  const [eventCounts, setEventCounts] = useState({});
  const navigate = useNavigate();

  // Initialize data on mount - fix dependency array to prevent infinite calls
  useEffect(() => {
    const initializeData = async () => {
      try {
        // Fetch initial events
        await fetchEvents();
        
        // Fetch featured events
        await fetchFeaturedEvents();
        
        // Fetch category counts
        const { data: categories } = await getEventCategories();
        setEventCounts(categories || {});
      } catch (err) {
        console.error('Error initializing event discovery:', err);
      }
    };

    initializeData();
  }, []); // Empty dependency array - only run once on mount

  // Update filtered events when events or filters change
  useEffect(() => {
    if (searchQuery) {
      // If there's a search query, use search results
      setFilteredEvents(events);
    } else {
      // Apply local filtering to events
      let filtered = [...events];

      // Category filter
      if (filters?.category && filters?.category !== 'all') {
        filtered = filtered?.filter(event => event?.category === filters?.category);
      }

      // Location filter
      if (filters?.location && filters?.location !== 'all') {
        filtered = filtered?.filter(event =>
          event?.location?.toLowerCase()?.includes(filters?.location?.replace('-', ' ')?.toLowerCase())
        );
      }

      // Price range filter
      if (filters?.priceRange && filters?.priceRange !== 'all') {
        filtered = filtered?.filter(event => {
          if (!event?.tiers || event?.tiers?.length === 0) {
            return filters?.priceRange?.includes('0');
          }
          
          const minPrice = Math.min(...event?.tiers?.map(t => t?.price || 0));
          
          if (filters?.priceRange === '500+') {
            return minPrice >= 500;
          }
          
          const [min, max] = filters?.priceRange?.split('-')?.map(Number);
          return minPrice >= min && minPrice <= max;
        });
      }

      // Date filter
      if (filters?.dateRange?.from) {
        const filterDate = new Date(filters?.dateRange?.from);
        filtered = filtered?.filter(event => {
          const eventDate = new Date(event?.date);
          return eventDate >= filterDate;
        });
      }

      setFilteredEvents(filtered);
    }
    
    setCurrentPage(1);
  }, [events, searchQuery, filters]);

  // Handle search
  const handleSearch = async (query) => {
    setSearchQuery(query);
    
    if (query?.trim()) {
      // Perform search with current filters
      await searchEvents(query, {
        category: filters?.category !== 'all' ? filters?.category : undefined,
        location: filters?.location !== 'all' ? filters?.location : undefined,
        priceRange: filters?.priceRange !== 'all' ? filters?.priceRange : undefined,
        dateRange: filters?.dateRange
      });
    } else {
      // Reset to all events if search is cleared
      await fetchEvents();
    }
  };

  // Handle filter changes
  const handleFilterChange = async (filterType, value) => {
    const newFilters = {
      ...filters,
      [filterType]: value
    };
    
    setFilters(newFilters);

    // If there's an active search, re-search with new filters
    if (searchQuery?.trim()) {
      await searchEvents(searchQuery, {
        category: newFilters?.category !== 'all' ? newFilters?.category : undefined,
        location: newFilters?.location !== 'all' ? newFilters?.location : undefined,
        priceRange: newFilters?.priceRange !== 'all' ? newFilters?.priceRange : undefined,
        dateRange: newFilters?.dateRange
      });
    } else {
      // Re-fetch events with new filters
      await fetchEvents({
        category: newFilters?.category !== 'all' ? newFilters?.category : undefined,
        location: newFilters?.location !== 'all' ? newFilters?.location : undefined,
        dateRange: newFilters?.dateRange
      });
    }
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    handleFilterChange('category', category);
  };

  // Handle event click
  const handleEventClick = (event) => {
    // Navigate to event details page with the event ID using query parameter
    navigate(`/event-details?id=${event?.id}`);
  };

  // Pagination
  const currentEvents = filteredEvents?.slice(0, currentPage * eventsPerPage);
  const hasMore = currentEvents?.length < filteredEvents?.length;

  const handleLoadMore = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setCurrentPage(prev => prev + 1);
      setLoadingMore(false);
    }, 500);
  };

  // Show error state
  if (error) {
    return (
      <>
        <Helmet>
          <title>Event Discovery Error - EventGrid</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <main className="pt-16">
            <div className="px-6 py-8 text-center">
              <h2 className="text-2xl font-bold text-foreground mb-4">
                Error Loading Events
              </h2>
              <p className="text-muted-foreground mb-6">
                {error}
              </p>
              <button
                onClick={() => fetchEvents()}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                Try Again
              </button>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Discover Premium Events - EventGrid</title>
        <meta name="description" content="Discover and book premium events with EventGrid. Browse concerts, conferences, workshops, and more in your area." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          {/* Search Bar */}
          <SearchBar
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            filters={filters}
            resultCount={filteredEvents?.length || 0}
            loading={loading}
          />

          <div className="px-6 py-8">
            {/* Featured Section */}
            {!searchQuery && filters?.category === 'all' && (
              <FeaturedSection 
                featuredEvents={featuredEvents} 
                loading={loading}
              />
            )}

            {/* Category Filter */}
            <CategoryFilter
              selectedCategory={filters?.category}
              onCategoryChange={handleCategoryChange}
              eventCounts={eventCounts}
              loading={loading}
            />

            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-foreground">
                  {searchQuery ? `Search Results for "${searchQuery}"` : 'All Events'}
                </h2>
                <p className="text-muted-foreground mt-1">
                  {filteredEvents?.length || 0} events found
                </p>
              </div>
            </div>

            {/* Event Grid */}
            <EventGrid
              events={currentEvents}
              loading={loading}
              className="mb-8"
              onEventClick={handleEventClick}
            />

            {/* Load More */}
            {!loading && filteredEvents?.length > 0 && (
              <LoadMoreButton
                onLoadMore={handleLoadMore}
                loading={loadingMore}
                hasMore={hasMore}
                currentCount={currentEvents?.length}
                totalCount={filteredEvents?.length}
              />
            )}

            {/* Empty State */}
            {!loading && filteredEvents?.length === 0 && (
              <div className="text-center py-12">
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  No Events Found
                </h3>
                <p className="text-muted-foreground mb-6">
                  {searchQuery 
                    ? `No events match your search for "${searchQuery}"`
                    : 'No events match your current filters'
                  }
                </p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setFilters({
                      category: 'all',
                      location: 'all',
                      priceRange: 'all',
                      dateRange: null
                    });
                    fetchEvents();
                  }}
                  className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default EventDiscovery;